import { useMemo, useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { Box, Button, CircularProgress, MenuItem, Select, Skeleton, Typography, useMediaQuery } from '@mui/material'
import Swal from 'sweetalert2'

import { getDashboard } from '../../../../services/api/AnalyticsDataService'
import { startCheckout } from '../../../../services/api/StripeDataService'
import SponsorReportsSection from './SponsorReportsSection'
import ProPromoCard from '../../../../utils/subscription/ProPromoCard'
import {
    SectionPaper, Stat, BarRow, Delta, Empty, ProPrompt,
    pct, num, countryFlag, prettyLabel, isoRange, formatPretty,
} from './analyticsUi'

// "Upgrade to Pro" → Pro monthly Stripe checkout (mirrors AddLinkButtonModal's handleUpgrade).
const upgradeToPro = async () => {
    try {
        await startCheckout('pro', true)
    } catch {
        Swal.fire({ title: "Can't Proceed to Checkout", text: 'Something went wrong. Please try again later.', icon: 'error' })
    }
}

// Money sections use a dollar-green palette so monetised data reads as "money" at a glance.
const MONEY_BORDER = '#bfe6cc'
const MONEY_DARK = '#0f5132'

const RANGES = [
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 14 days', days: 14 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 90 days', days: 90 },
]

export default function AnalyticsTab({ page }) {
    const pageId = page?.pageId
    const isMobile = useMediaQuery('(max-width:1050px)')
    const [rangeIdx, setRangeIdx] = useState(1) // default: Last 30 days

    const range = useMemo(() => isoRange(RANGES[rangeIdx].days), [rangeIdx])

    const { data, isLoading, isError, isFetching, refetch } = useQuery({
        queryKey: ['receipts-dashboard', pageId, range.from, range.to],
        queryFn: () => getDashboard(pageId, range),
        enabled: !!pageId,
        placeholderData: keepPreviousData, // keep the old range visible while the new one loads
    })

    if (!pageId) {
        return <Typography sx={{ p: 2 }}>No page yet.</Typography>
    }
    if (isLoading) {
        return <DashboardSkeleton />
    }
    if (isError || !data) {
        return <ErrorState onRetry={refetch} />
    }

    const isPro = data.plan === 'PRO'
    const o = data.overview || {}
    const tookNoAction = 1 - (o.tookActionRate || 0)
    const showNoAction = tookNoAction < 0.4 // only surface it when most visitors did take action

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
            {/* Date row — date range in one corner, range selector in the other, no paper background */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mx: isMobile ? '0.75rem' : 2, my: 1 }}>
                <Typography sx={{ fontWeight: 500, fontSize: '1rem', color: '#222' }}>
                    {formatPretty(data.from)} → {formatPretty(data.to)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isFetching && <CircularProgress size={16} thickness={5} />}
                    {/* Date range is a Pro control; Free is fixed to the backend-clamped 7-day window. */}
                    {isPro && (
                        <Select size="small" value={rangeIdx} onChange={(e) => setRangeIdx(e.target.value)}
                            sx={{ borderRadius: 2, fontSize: '.85rem' }}>
                            {RANGES.map((r, i) => <MenuItem key={r.days} value={i}>{r.label}</MenuItem>)}
                        </Select>
                    )}
                </Box>
            </Box>

            {/* Overview */}
            <SectionPaper title="Overview">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', columnGap: { xs: 3, sm: 4 }, rowGap: 2 }}>
                    <Stat value={num(o.visitors)} label="visitors" />
                    <Stat
                        value={<>{pct(o.tookActionRate)}<Delta value={o.tookActionRateDelta} /></>}
                        label="took action"
                    />
                    <Stat value={`${o.avgWatchSeconds || 0}s`} label="avg watch" />
                    {showNoAction && <Stat value={pct(tookNoAction)} label="took no action" valueSx={{ color: '#b00020' }} />}
                </Box>
            </SectionPaper>

            {/* Revenue Links */}
            <MoneyBanner money={data.money} isPro={isPro} />

            {/* Link Performance Rankings — Free sees the #1 link only, no money badge */}
            {(() => {
                const rows = data.topLinks || []
                if (!rows.length) {
                    return (
                        <SectionPaper title="Link Performance Rankings">
                            <Empty text={isPro ? 'No clicks in this range yet.' : 'Share your Vibio link to start tracking clicks.'} />
                        </SectionPaper>
                    )
                }
                return (
                    <SectionPaper title="Link Performance Rankings">
                        <TopLinks rows={isPro ? rows : rows.slice(0, 1)} showMoney={isPro} />
                        {!isPro && <ProPrompt text="See all link rankings with Pro." />}
                    </SectionPaper>
                )
            })()}

            {/* Audience Discovery — hidden for Free when there's no data */}
            {(() => {
                const rows = data.sources || []
                return (
                    <SectionPaper title="Audience Discovery">
                        <BarList rows={isPro ? rows : rows.slice(0, 1)}
                            render={(r) => ({ label: prettyLabel(r.label), share: r.share })}
                            empty="Not enough audience discovery data yet." />
                        {!isPro && rows.length > 0 && <ProPrompt text="There's more to discover about your audience. Learn every detail with Pro." />}
                    </SectionPaper>
                )
            })()}

            {/* Audience Origins */}
            {(() => {
                const rows = data.locations || []
                return (
                    <SectionPaper title="Audience Locations">
                        {isPro ?
                            <BarList rows={rows}
                                render={(r) => ({ label: r.label, leading: countryFlag(r.label), share: r.share })}
                                empty="Not enough location data yet." />
                            :
                            <ProPrompt text="Learn all about where your audience is coming to visit you with Pro." />
                        }
                    </SectionPaper>
                )
            })()}

            {/* Audience Devices */}
            {(() => {
                const rows = data.devices || []
                return (
                    <SectionPaper title="Audience Devices">
                        {isPro ?
                            <BarList rows={rows}
                                render={(r) => ({ label: prettyLabel(r.label), share: r.share })}
                                empty="No device data yet." />
                            :
                            <ProPrompt text="See what devices your audience is on with Pro." />
                        }
                    </SectionPaper>
                )
            })()}

            {/* Brand Reports */}
            <SponsorReportsSection pageId={pageId} links={page?.links || []} locked={!isPro} />

            {/* Pro promo — Free users who've started getting visitors */}
            {!isPro && o.visitors > 0 && <ProPromoCard />}
        </Box>
    )
}

function MoneyBanner({ money, isPro }) {
    if (!isPro) {
        return (
            <SectionPaper title="Revenue Links" sx={{ bgcolor: '#fff', pt: 2.5, pb: 2.5 }}>
                <Typography sx={{ fontSize: '.92rem', color: '#555' }}>
                    Storefronts, shops, affiliates, and sponsors. Track which earning links get the most clicks.{' '}
                    <Box component="span" onClick={upgradeToPro}
                        sx={{ color: '#ff1493', fontWeight: 500, cursor: 'pointer' }}>
                        Start tracking with Pro
                    </Box>{' '}
                </Typography>
            </SectionPaper>
        )
    }
    return (
        <SectionPaper title="Revenue Links" sx={{ bgcolor: '#fff', pt: 2.5, pb: 2.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', columnGap: 1.2, rowGap: 0.5 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '1.5rem', lineHeight: 1, color: MONEY_DARK }}>
                    {num(money?.moneyClicks)}
                    {money && <Delta value={money.delta} relative small />}
                </Typography>
                <Typography sx={{ fontSize: '.85rem', color: '#888' }}>
                    <b>{pct(money?.moneyClickShare)}</b> of all clicks went to links that help you earn
                </Typography>
            </Box>
        </SectionPaper>
    )
}

function TopLinks({ rows, showMoney = true }) {
    if (!rows.length) return <Empty text="No clicks in this range yet." />
    return (
        <Box>
            <Box sx={{ display: 'flex', fontSize: '.6rem', color: '#aaa', fontWeight: 700, mb: 1, px: 0.5, gap: 1 }}>
                <Box sx={{ width: 20 }}>#</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>LINK</Box>
                <Box sx={{ width: 56, textAlign: 'right' }}>CLICKS</Box>
                <Box sx={{ width: 50, textAlign: 'right' }}>CTR</Box>
            </Box>
            {rows.map((r) => (
                <Box key={r.rank} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.9, borderTop: '1px solid #f0f0f0' }}>
                    <Box sx={{ width: 20, color: '#bbb' }}>{r.rank}</Box>
                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Typography
                            component="span"
                            title={r.label}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: '.9rem',
                                display: 'block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {r.label}
                        </Typography>
                        <>
                            {showMoney && r.money &&
                                <span style={{ padding: ".2rem", borderRadius: '20px', background: MONEY_BORDER, color: MONEY_DARK, fontSize: 12, fontFamily: 'monospace' }}>
                                    $
                                </span>}
                        </>
                    </Box>
                    <Box sx={{ width: 56, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{num(r.clicks)}</Box>
                    <Box sx={{ width: 50, textAlign: 'right', fontWeight: 700, color: '#333' }}>{pct(r.ctr)}</Box>
                </Box>
            ))}
        </Box>
    )
}

function BarList({ rows, render, empty }) {
    if (!rows.length) return <Empty text={empty || 'Not enough data yet.'} />
    return <>{rows.map((r, i) => { const p = render(r); return <BarRow key={i} {...p} /> })}</>
}

function DashboardSkeleton() {
    const isMobile = useMediaQuery('(max-width:1050px)')
    const block = (h) => <Skeleton variant="rounded" height={h} sx={{ m: isMobile ? '0.75rem' : 2, borderRadius: '30px' }} />
    return (
        <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mx: isMobile ? '0.75rem' : 2, my: 1 }}>
                <Skeleton variant="text" width={190} height={28} />
                <Skeleton variant="rounded" width={130} height={36} />
            </Box>
            {block(120)}
            {block(110)}
            {block(180)}
            {block(150)}
            {block(150)}
        </Box>
    )
}

function ErrorState({ onRetry }) {
    return (
        <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
            <SectionPaper>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Couldn't load data.</Typography>
                    <Typography sx={{ color: '#777', fontSize: '.9rem', mb: 2 }}>Check your connection and try again.</Typography>
                    <Button onClick={onRetry} variant="contained"
                        sx={{ textTransform: 'none', borderRadius: '40px', boxShadow: '4px 5px 0px #000' }}>
                        Retry
                    </Button>
                </Box>
            </SectionPaper>
        </Box>
    )
}
